import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    FileText, ChevronLeft, CheckCircle, Star,
    User, MapPin, Building2, Wallet, History,
    Phone, Calendar, MessageSquare, Trophy, AlertCircle, X,
    Activity, StickyNote, TrendingUp, Home, RefreshCw, MoreHorizontal,
} from 'lucide-react';
import { useCRM } from '../context/CRMContext';
import { formatDate, toInputDate } from '../utils/dateUtils';

// ─── CONSTANTS ─────────────────────────────────────────────────────────────────

const STAGES = [
    'New Inquiry',
    'Site Visit / Estimation',
    'Proposal Sent',
    'Negotiation',
    'Won / Contract',
    'Lost'
];

const STAGE_META = {
    'New Inquiry':              { color: '#6366F1', bg: '#EEF2FF', dot: '#6366F1' },
    'Site Visit / Estimation':  { color: '#F59E0B', bg: '#FFFBEB', dot: '#F59E0B' },
    'Proposal Sent':            { color: '#06B6D4', bg: '#ECFEFF', dot: '#06B6D4' },
    'Negotiation':              { color: '#8B5CF6', bg: '#F5F3FF', dot: '#8B5CF6' },
    'Won / Contract':           { color: '#10B981', bg: '#D1FAE5', dot: '#10B981' },
    'Lost':                     { color: '#DC2626', bg: '#FEF2F2', dot: '#EF4444' },
    // Aliases for compatibility
    'Contacted':                { color: '#2563EB', bg: '#EFF6FF', dot: '#3B82F6' },
    'Site Visit':               { color: '#F59E0B', bg: '#FFFBEB', dot: '#F59E0B' },
    'Booked':                   { color: '#10B981', bg: '#D1FAE5', dot: '#10B981' },
    'Sold':                     { color: '#10B981', bg: '#D1FAE5', dot: '#10B981' },
    'Won':                      { color: '#10B981', bg: '#D1FAE5', dot: '#10B981' },
};

const LOST_REASONS = [
    'Price too high',
    'Location issue',
    'Bought from competitor',
    'Plan / unit size issue',
    'Postponed decision',
    'Not interested / Junk',
];

// ─── SMALL ATOMS ───────────────────────────────────────────────────────────────

const StageBadge = ({ stage }) => {
    const m = STAGE_META[stage] || STAGE_META['New Inquiry'];
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: m.bg, color: m.color,
            border: `1px solid ${m.dot}44`, borderRadius: 20,
            padding: '1px 7px', fontSize: 10, fontWeight: 700, letterSpacing: '0.02em',
        }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: m.dot }} />
            {stage}
        </span>
    );
};

const FieldRow = ({ label, children, tight }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '0 8px', alignItems: tight ? 'flex-start' : 'center', marginBottom: 9 }}>
        <span style={{ fontSize: 10.5, color: '#6B7280', fontWeight: 500, paddingTop: tight ? 3 : 0 }}>{label}</span>
        <div style={{ fontSize: 11.5, color: '#111827' }}>{children}</div>
    </div>
);

const LineInput = ({ name, value, onChange, onBlur, disabled, placeholder, type = 'text', bold }) => (
    <input
        type={type} name={name} value={value || ''} onChange={onChange} onBlur={onBlur}
        disabled={disabled} placeholder={placeholder}
        style={{
            width: '100%', border: 'none', borderBottom: `1px solid ${disabled ? '#F3F4F6' : '#E5E7EB'}`,
            outline: 'none', background: 'transparent', padding: '2px 0',
            fontSize: 11.5, color: disabled ? '#9CA3AF' : '#111827',
            fontWeight: bold ? 600 : 400, cursor: disabled ? 'not-allowed' : 'text', fontFamily: 'inherit',
        }}
    />
);

const SectionHead = ({ icon: Icon, title }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 12, paddingBottom: 7, borderBottom: '1px solid #F3F4F6' }}>
        {Icon && <Icon size={12} color="#6366F1" />}
        <span style={{ fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{title}</span>
    </div>
);

const Card = ({ children, style, className = "" }) => (
    <div className={`bg-white dark:bg-[#161b22] border border-[#E5E7EB] dark:border-[#30363d] rounded-lg p-3.5 text-slate-900 dark:text-slate-100 shadow-xs ${className}`} style={style}>
        {children}
    </div>
);

const Overlay = ({ children }) => (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        {children}
    </div>
);

const ModalBox = ({ children, style, className = "" }) => (
    <div className={`bg-white dark:bg-[#161b22] border border-slate-200 dark:border-[#30363d] rounded-xl w-full overflow-hidden shadow-xl ${className}`} style={style}>
        {children}
    </div>
);

const ModalHead = ({ title, color, onClose }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: color + '12', borderBottom: `1px solid ${color}20` }}>
        <span style={{ fontSize: 12, fontWeight: 700, color }}>{title}</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 2 }}><X size={13} /></button>
    </div>
);

const ModalFoot = ({ children }) => (
    <div className="flex justify-end gap-2 px-3.5 py-2.5 bg-slate-50 dark:bg-[#21262d] border-t border-slate-100 dark:border-[#30363d]">
        {children}
    </div>
);

const BtnCancel = ({ onClick, label = 'Cancel' }) => (
    <button onClick={onClick} style={{ fontSize: 11, color: 'var(--text-sub, #6B7280)', background: 'var(--bg-card, #fff)', border: '1px solid var(--border-card, #E5E7EB)', borderRadius: 5, padding: '4px 12px', cursor: 'pointer', fontFamily: 'inherit' }}>{label}</button>
);

const BtnPrimary = ({ onClick, label, color = '#4F46E5', disabled }) => (
    <button onClick={onClick} disabled={disabled} style={{ fontSize: 11, fontWeight: 600, color: '#fff', background: color, border: 'none', borderRadius: 5, padding: '4px 14px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1, fontFamily: 'inherit' }}>{label}</button>
);

const StatPill = ({ label, value, color }) => (
    <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 9.5, color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 700, color }}>{value}</div>
    </div>
);

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────────

/**
 * Works in two modes:
 *  1. Full page  — opened via React Router. Reads lead from location.state.lead
 *  2. Side panel — rendered directly with <CRMDetailPage lead={...} onBack={...} isSidePanel />
 *
 * BUG FIX: Previously only used useLocation, so side-panel mode always had lead = undefined.
 */
const CRMDetailPage = ({ lead: leadProp, onBack, isSidePanel }) => {
    const { updateLead, getLeadActivities, addLeadActivity, fetchLeadById } = useCRM();

    // BUG FIX: prefer prop, fall back to router state or direct query fetch
    const location = useLocation();
    const navigate = useNavigate();
    const [fetchedLead, setFetchedLead] = useState(null);
    const loadedIdRef = useRef(null);

    const queryId = new URLSearchParams(location.search).get('id');
    const lead = leadProp ?? location.state?.lead ?? fetchedLead;

    useEffect(() => {
        if (!leadProp && !location.state?.lead && queryId) {
            if (loadedIdRef.current === String(queryId)) return;
            loadedIdRef.current = String(queryId);
            fetchLeadById(queryId).then(res => {
                if (res) {
                    const l = res.data || res;
                    setFetchedLead(l);
                }
            });
        }
    }, [queryId, leadProp, location.state, fetchLeadById]);

    const getInitialStage = () => {
        if (!lead?.stage) return 'New Inquiry';
        if (STAGES.includes(lead.stage)) return lead.stage;
        return 'New Inquiry';
    };

    const [currentStage, setCurrentStage] = useState(getInitialStage);
    const [activeTab, setActiveTab] = useState('notes');
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [newNote, setNewNote] = useState('');
    const [activities, setActivities] = useState([]);
    const [actLoading, setActLoading] = useState(false);
    const [noteLoading, setNoteLoading] = useState(false);

    const [showLostModal, setShowLostModal] = useState(false);
    const [lostReason, setLostReason] = useState(LOST_REASONS[0]);
    const [lostRemarks, setLostRemarks] = useState('');
    const [showSoldModal, setShowSoldModal] = useState(false);
    const [validationMsg, setValidationMsg] = useState(null);
    const [toast, setToast] = useState(null);

    const [formData, setFormData] = useState({
        clientName: '', phone: '', email: '', address: '',
        source: 'Walk-in', interestedUnit: '', configuration: '3 BHK',
        preferredFloor: 'Any', budget: '', finalPrice: '',
        discount: '', tokenAmount: '', bookingDate: '', priority: 'medium',
    });

    const isProfileLocked = ['Proposal Sent', 'Negotiation', 'Won / Contract', 'Won', 'Sold', 'Booked', 'Lost'].includes(currentStage);
    const isTerminal = ['Won / Contract', 'Won', 'Sold', 'Lost'].includes(currentStage);

    // Populate form from lead
    useEffect(() => {
        if (lead) {
            setFormData(prev => ({
                ...prev,
                clientName: lead.client || lead.client_name || '',
                phone: lead.phone || '',
                email: lead.email || '',
                budget: lead.budget || lead.revenue || '',
                interestedUnit: lead.unit || lead.interested_unit || '',
                priority: lead.priority || 'medium',
                configuration: lead.configuration || '3 BHK',
                finalPrice: lead.finalPrice || '',
                tokenAmount: lead.tokenAmount || '',
                bookingDate: toInputDate(lead.bookingDate || lead.booking_date),
                source: lead.source || 'Walk-in',
            }));
            if (lead.stage) setCurrentStage(lead.stage);
        }
    }, [lead]);

    // Load activities when tab switches
    useEffect(() => {
        if (lead?.id && activeTab === 'activity') {
            setActLoading(true);
            getLeadActivities(lead.id)
                .then(d => setActivities(d || []))
                .finally(() => setActLoading(false));
        }
    }, [lead?.id, activeTab, currentStage, getLeadActivities]);

    // ─── HELPERS ─────────────────────────────────────────────────────────────

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3500);
    };

    const handleInput = (e) => {
        const { name, value } = e.target;
        setFormData(p => ({ ...p, [name]: value }));
        setIsDirty(true);
    };

    const getPayload = () => ({
        client: formData.clientName,
        phone: formData.phone,
        email: formData.email,
        budget: parseFloat(formData.budget) || 0,
        unit: formData.interestedUnit,
        priority: formData.priority,
        configuration: formData.configuration,
        source: formData.source,
        finalPrice: formData.finalPrice,
        tokenAmount: formData.tokenAmount,
        bookingDate: formData.bookingDate,
    });

    const handleAutoSave = async () => {
        if (!lead?.id || !isDirty) return;
        setIsSaving(true);
        await updateLead(lead.id, getPayload());
        setIsDirty(false);
        setIsSaving(false);
    };

    const stageChange = async (newStage) => {
        if (!lead?.id || newStage === currentStage) return;
        setCurrentStage(newStage);
        await updateLead(lead.id, { ...getPayload(), stage: newStage });
        setIsDirty(false);
        showToast(`Stage → ${newStage}`);
    };

    const handleAddNote = async () => {
        if (!newNote.trim() || !lead?.id) return;
        setNoteLoading(true);
        await addLeadActivity(lead.id, { type: 'Note', summary: 'Note added', notes: newNote });
        setNewNote('');
        const d = await getLeadActivities(lead.id);
        setActivities(d || []);
        setNoteLoading(false);
    };

    // Stage action handlers
    const handleSiteVisit = () => {
        stageChange('Site Visit / Estimation');
    };

    const handleProposal = () => {
        stageChange('Proposal Sent');
    };

    const handleNegotiation = () => {
        stageChange('Negotiation');
    };

    const handleConfirmLost = () => {
        updateLead(lead.id, { ...getPayload(), lostReason, lostRemarks, stage: 'Lost' });
        setCurrentStage('Lost');
        setShowLostModal(false);
        setIsDirty(false);
        showToast('Marked as lost', 'error');
    };

    const confirmSold = () => {
        stageChange('Won / Contract');
        setShowSoldModal(false);
        showToast('Deal won / contract finalized!');
    };

    // Primary CTA config
    const primaryCTA = (() => {
        if (currentStage === 'New Inquiry')  return { label: 'Schedule Site Visit / Estimation', icon: Calendar,      action: handleSiteVisit,    color: '#F59E0B' };
        if (currentStage === 'Site Visit / Estimation' || currentStage === 'Site Visit') return { label: 'Send Proposal', icon: FileText, action: handleProposal, color: '#06B6D4' };
        if (currentStage === 'Proposal Sent')           return { label: 'Start Negotiation',                   icon: MessageSquare, action: handleNegotiation, color: '#8B5CF6' };
        if (currentStage === 'Negotiation')             return { label: 'Win Deal / Contract',                 icon: Trophy,        action: () => setShowSoldModal(true), color: '#10B981' };
        return null;
    })();

    const handleBack = () => {
        if (isSidePanel && onBack) { onBack(); }
        else navigate('/crm');
    };

    // ─── RENDER ──────────────────────────────────────────────────────────────

    if (!lead) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 32 }}>
                <p style={{ color: '#9CA3AF', fontSize: 13 }}>No lead selected.</p>
            </div>
        );
    }

    const padding = isSidePanel ? 14 : 20;

    return (
        <div className="flex flex-col min-h-full bg-[#F9FAFB] dark:bg-[#0d1117] text-slate-900 dark:text-slate-100 text-xs relative transition-colors duration-200">

            {/* ── TOP BAR ── */}
            {!isSidePanel && (
                <div className="bg-white dark:bg-[#161b22] border-b border-[#E5E7EB] dark:border-[#30363d] px-5 h-11 flex items-center justify-between sticky top-0 z-30 transition-colors duration-200">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <button onClick={handleBack} className="flex items-center gap-1 text-[11px] text-[#6B7280] dark:text-slate-400 hover:text-slate-900 dark:hover:text-white bg-transparent border-0 cursor-pointer font-medium">
                            <ChevronLeft size={13} /> CRM Pipeline
                        </button>
                        <span className="text-[#E5E7EB] dark:text-slate-600">›</span>
                        <span className="text-[11.5px] font-semibold text-[#374151] dark:text-white">{formData.clientName || 'New Lead'}</span>
                        <StageBadge stage={currentStage} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {['Booked', 'Sold'].includes(currentStage) && (
                            <>
                                <NavChip icon={<FileText size={12} color="#7C3AED" />} label="Docs" />
                                <NavChip icon={<Wallet size={12} color="#059669" />} label={`₹ ${formData.tokenAmount || '—'}`} />
                                <NavChip icon={<History size={12} color="#F59E0B" />} label="History" />
                            </>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10.5, color: isSaving ? '#F59E0B' : '#10B981' }}>
                            {isSaving ? <RefreshCw size={10} /> : <CheckCircle size={10} />}
                            {isSaving ? 'Saving…' : 'Saved'}
                        </div>
                    </div>
                </div>
            )}

            {/* ── STAGE STEPPER ── */}
            <div style={{ background: 'var(--bg-card, #fff)', borderBottom: '1px solid var(--border-card, #E5E7EB)', padding: `10px ${padding}px`, position: isSidePanel ? 'relative' : 'sticky', top: isSidePanel ? 'auto' : 44, zIndex: 20 }}>
                {/* Step indicators */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, overflowX: 'auto', paddingBottom: 2 }}>
                    {STAGES.filter(s => s !== 'Lost').map((stage, i, arr) => {
                        const idx = arr.indexOf(stage);
                        const curIdx = arr.indexOf(currentStage === 'Lost' ? arr[0] : currentStage);
                        const isDone = idx < curIdx;
                        const isActive = stage === currentStage;
                        const m = STAGE_META[stage];
                        return (
                            <React.Fragment key={stage}>
                                <button
                                    onClick={() => !isTerminal && stageChange(stage)}
                                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, background: 'none', border: 'none', cursor: isTerminal ? 'default' : 'pointer', padding: '0 3px', flexShrink: 0 }}
                                >
                                    <div style={{ width: 18, height: 18, borderRadius: '50%', background: isActive ? m.color : isDone ? m.dot + '33' : '#F3F4F6', border: `2px solid ${isActive ? m.color : isDone ? m.dot : '#D1D5DB'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                                        {isDone
                                            ? <CheckCircle size={9} color={m.color} />
                                            : <span style={{ width: 5, height: 5, borderRadius: '50%', background: isActive ? '#fff' : '#D1D5DB' }} />
                                        }
                                    </div>
                                    <span style={{ fontSize: 9, color: isActive ? m.color : isDone ? '#6B7280' : '#9CA3AF', fontWeight: isActive ? 700 : 500, whiteSpace: 'nowrap' }}>{stage}</span>
                                </button>
                                {i < arr.length - 1 && (
                                    <div style={{ flex: 1, height: 1.5, background: idx < curIdx ? '#6366F122' : 'var(--border-card, #E5E7EB)', margin: '0 2px', marginBottom: 14, borderRadius: 1, minWidth: 8 }} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>

                {/* Actions row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {currentStage === 'Sold' ? (
                            <span style={{ fontSize: 10.5, fontWeight: 700, color: '#047857', background: '#D1FAE5', padding: '3px 10px', borderRadius: 20, border: '1px solid #A7F3D0' }}>Deal Closed</span>
                        ) : currentStage === 'Lost' ? (
                            <span style={{ fontSize: 10.5, fontWeight: 700, color: '#DC2626', background: '#FEE2E2', padding: '3px 10px', borderRadius: 20, border: '1px solid #FECACA' }}>Marked Lost</span>
                        ) : primaryCTA && (
                            <button onClick={primaryCTA.action} style={{ display: 'flex', alignItems: 'center', gap: 4, background: primaryCTA.color, color: '#fff', border: 'none', borderRadius: 5, padding: '4px 11px', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                                <primaryCTA.icon size={11} />
                                {primaryCTA.label}
                            </button>
                        )}
                        {!isTerminal && (
                            <button onClick={() => setShowLostModal(true)} style={{ background: 'var(--bg-card, #fff)', border: '1px solid var(--border-card, #E5E7EB)', color: 'var(--text-sub, #6B7280)', borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
                                Mark Lost
                            </button>
                        )}
                    </div>
                    <span style={{ fontSize: 10, color: '#9CA3AF', flexShrink: 0 }}>
                        {lead?.createdAt
                            ? new Date(lead.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                            : 'Nov 24, 2024'}
                    </span>
                </div>
            </div>

            {/* ── BODY ── */}
            <div style={{ flex: 1, padding: `14px ${padding}px` }}>

                {/* Lead summary strip */}
                <Card style={{ marginBottom: 12, padding: '10px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: '#4F46E5', flexShrink: 0 }}>
                                {(formData.clientName || 'N').charAt(0).toUpperCase()}
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{formData.clientName || 'New Inquiry'}</span>
                                    <Star size={11} fill={formData.priority === 'high' ? '#F59E0B' : 'none'} color={formData.priority === 'high' ? '#F59E0B' : '#D1D5DB'} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
                                    {formData.phone && <span style={{ fontSize: 10, color: '#6B7280' }}>{formData.phone}</span>}
                                    {formData.email && <span style={{ fontSize: 10, color: '#6B7280' }}>{formData.email}</span>}
                                    <span style={{ fontSize: 10, color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: 2 }}><MapPin size={9} />South Bopal</span>
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 14, flexShrink: 0 }}>
                            <StatPill label="Budget" value={formData.budget ? `₹ ${parseInt(formData.budget).toLocaleString('en-IN')}` : '—'} color="#7C3AED" />
                            <StatPill label="Priority" value={formData.priority === 'high' ? 'High' : formData.priority === 'low' ? 'Low' : 'Medium'} color={formData.priority === 'high' ? '#059669' : '#D97706'} />
                        </div>
                    </div>
                </Card>

                {/* Two column form */}
                <div style={{ display: 'grid', gridTemplateColumns: isSidePanel ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 12 }}>

                    {/* Customer Details */}
                    <Card>
                        <SectionHead icon={User} title="Customer Details" />
                        <FieldRow label="Full Name">
                            <LineInput name="clientName" value={formData.clientName} onChange={handleInput} onBlur={handleAutoSave} disabled={isProfileLocked} placeholder="Client name" bold />
                        </FieldRow>
                        <FieldRow label="Mobile">
                            <LineInput name="phone" value={formData.phone} onChange={handleInput} onBlur={handleAutoSave} disabled={isProfileLocked} placeholder="+91 00000 00000" />
                        </FieldRow>
                        <FieldRow label="Email">
                            <LineInput name="email" value={formData.email} onChange={handleInput} onBlur={handleAutoSave} disabled={isProfileLocked} placeholder="email@example.com" />
                        </FieldRow>
                        <FieldRow label="Address" tight>
                            <textarea
                                name="address" value={formData.address || ''} rows={2}
                                onChange={handleInput} onBlur={handleAutoSave} disabled={isProfileLocked}
                                placeholder="Current residence…"
                                style={{ width: '100%', fontSize: 11.5, border: '1px solid #E5E7EB', borderRadius: 5, padding: '4px 6px', outline: 'none', resize: 'none', background: isProfileLocked ? '#F9FAFB' : '#fff', color: isProfileLocked ? '#9CA3AF' : '#374151', fontFamily: 'inherit', boxSizing: 'border-box' }}
                            />
                        </FieldRow>
                        <FieldRow label="Lead Source">
                            <select name="source" value={formData.source} onChange={handleInput} onBlur={handleAutoSave} disabled={isProfileLocked}
                                style={{ fontSize: 11.5, border: 'none', borderBottom: '1px solid #E5E7EB', background: 'transparent', outline: 'none', padding: '2px 0', color: isProfileLocked ? '#9CA3AF' : '#111827', width: '100%', fontFamily: 'inherit' }}>
                                {['Walk-in', 'Website', 'Referral', 'Broker', 'Social Media', 'IVR / Call'].map(s => <option key={s}>{s}</option>)}
                            </select>
                        </FieldRow>
                        <FieldRow label="Priority">
                            <div style={{ display: 'flex', gap: 10 }}>
                                {['low', 'medium', 'high'].map(p => (
                                    <label key={p} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#374151', cursor: 'pointer' }}>
                                        <input type="radio" name="priority" value={p} checked={formData.priority === p} onChange={handleInput} style={{ accentColor: '#6366F1' }} />
                                        {p.charAt(0).toUpperCase() + p.slice(1)}
                                    </label>
                                ))}
                            </div>
                        </FieldRow>
                    </Card>

                    {/* Property + Deal */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <Card>
                            <SectionHead icon={Home} title="Property Interest" />
                            <FieldRow label="Unit / Flat">
                                <LineInput name="interestedUnit" value={formData.interestedUnit} onChange={handleInput} onBlur={handleAutoSave} disabled={isProfileLocked} placeholder="e.g. A-402" bold />
                            </FieldRow>
                            <FieldRow label="Configuration">
                                <div style={{ display: 'flex', gap: 10 }}>
                                    {['2 BHK', '3 BHK', 'Penthouse'].map(opt => (
                                        <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, opacity: isProfileLocked ? 0.6 : 1, cursor: isProfileLocked ? 'not-allowed' : 'pointer' }}>
                                            <input type="radio" name="configuration" value={opt} checked={formData.configuration === opt} onChange={handleInput} disabled={isProfileLocked} style={{ accentColor: '#6366F1' }} />
                                            {opt}
                                        </label>
                                    ))}
                                </div>
                            </FieldRow>
                            <FieldRow label="Preferred Floor">
                                <select name="preferredFloor" value={formData.preferredFloor} onChange={handleInput} onBlur={handleAutoSave}
                                    style={{ fontSize: 11.5, border: 'none', borderBottom: '1px solid #E5E7EB', background: 'transparent', outline: 'none', padding: '2px 0', width: '100%', fontFamily: 'inherit' }}>
                                    {['Any', 'Low (1–5)', 'Mid (6–12)', 'High (13+)'].map(s => <option key={s}>{s}</option>)}
                                </select>
                            </FieldRow>
                            <FieldRow label="Budget">
                                <LineInput name="budget" value={formData.budget} onChange={handleInput} onBlur={handleAutoSave} disabled={isProfileLocked} placeholder="₹ 0" />
                            </FieldRow>
                        </Card>

                        {['Negotiation', 'Booked', 'Sold'].includes(currentStage) && (
                            <Card style={{ borderLeft: '3px solid #6366F1' }}>
                                <SectionHead icon={TrendingUp} title="Deal Closure" />
                                <FieldRow label="Final Price">
                                    <LineInput name="finalPrice" value={formData.finalPrice} onChange={handleInput} onBlur={handleAutoSave} placeholder="₹ 0.00" bold />
                                </FieldRow>
                                <FieldRow label="Token Amount">
                                    <LineInput name="tokenAmount" value={formData.tokenAmount} onChange={handleInput} onBlur={handleAutoSave} placeholder="₹ 0.00" />
                                </FieldRow>
                                <FieldRow label="Discount">
                                    <LineInput name="discount" value={formData.discount} onChange={handleInput} onBlur={handleAutoSave} placeholder="e.g. 2%" />
                                </FieldRow>
                                <FieldRow label="Booking Date">
                                    <input type="date" name="bookingDate" value={formData.bookingDate || ''} onChange={handleInput} onBlur={handleAutoSave}
                                        style={{ fontSize: 11.5, border: 'none', borderBottom: '1px solid #E5E7EB', background: 'transparent', outline: 'none', padding: '2px 0', width: '100%', fontFamily: 'inherit' }} />
                                </FieldRow>
                            </Card>
                        )}
                    </div>
                </div>

                {/* ── TABS ── */}
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB' }}>
                        {[
                            { key: 'notes',    label: 'Internal Notes', icon: StickyNote },
                            { key: 'activity', label: 'Activity Log',   icon: Activity },
                        ].map(tab => (
                            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                                display: 'flex', alignItems: 'center', gap: 4,
                                padding: '8px 14px', fontSize: 11, fontWeight: 600,
                                color: activeTab === tab.key ? '#6366F1' : '#6B7280',
                                background: 'none', border: 'none', cursor: 'pointer',
                                borderBottom: `2px solid ${activeTab === tab.key ? '#6366F1' : 'transparent'}`,
                                marginBottom: -1, fontFamily: 'inherit',
                            }}>
                                <tab.icon size={11} />
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div style={{ padding: 14 }}>
                        {activeTab === 'notes' && (
                            <div>
                                <textarea
                                    placeholder="Add an internal note — site visit observations, negotiation details, follow-up tasks…"
                                    rows={4}
                                    style={{ width: '100%', fontSize: 11.5, border: '1px solid #E5E7EB', borderRadius: 6, padding: '8px 10px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', color: '#374151', lineHeight: 1.6, boxSizing: 'border-box' }}
                                />
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 7 }}>
                                    <BtnPrimary label="Save Note" color="#6366F1" />
                                </div>
                            </div>
                        )}

                        {activeTab === 'activity' && (
                            <div>
                                <div style={{ display: 'flex', gap: 7, marginBottom: 12 }}>
                                    <input
                                        type="text" value={newNote}
                                        onChange={e => setNewNote(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleAddNote()}
                                        placeholder="Log activity — called client, sent brochure…"
                                        style={{ flex: 1, fontSize: 11.5, border: '1px solid #E5E7EB', borderRadius: 5, padding: '5px 9px', outline: 'none', fontFamily: 'inherit' }}
                                    />
                                    <BtnPrimary onClick={handleAddNote} label={noteLoading ? '…' : 'Add'} color="#6366F1" disabled={noteLoading || !newNote.trim()} />
                                </div>

                                {actLoading ? (
                                    <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 11.5, padding: '12px 0' }}>Loading…</p>
                                ) : activities.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '16px 0' }}>
                                        <Activity size={20} color="#E5E7EB" style={{ margin: '0 auto 5px' }} />
                                        <p style={{ fontSize: 11.5, color: '#9CA3AF', margin: 0 }}>No activity yet.</p>
                                    </div>
                                ) : (
                                    <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                                        {activities.map(act => (
                                            <div key={act.id} style={{ display: 'flex', gap: 9, padding: '7px 0', borderBottom: '1px solid #F9FAFB', alignItems: 'flex-start' }}>
                                                <div style={{ width: 26, height: 26, borderRadius: '50%', background: act.activity_type === 'System' ? '#F3F4F6' : '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8.5, fontWeight: 700, color: act.activity_type === 'System' ? '#6B7280' : '#4F46E5', flexShrink: 0 }}>
                                                    {act.activity_type === 'System' ? 'SYS' : 'USR'}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                                        <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{act.summary || act.activity_type}</span>
                                                        <span style={{ fontSize: 9.5, color: '#9CA3AF', flexShrink: 0 }}>
                                                            {formatDate(act.created_at)}
                                                        </span>
                                                    </div>
                                                    {act.notes && <p style={{ margin: '2px 0 0', fontSize: 10.5, color: '#6B7280', lineHeight: 1.5 }}>{act.notes}</p>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </Card>
            </div>

            {/* ── MODALS ── */}

            {showLostModal && (
                <Overlay>
                    <ModalBox style={{ maxWidth: 380 }}>
                        <ModalHead title="Mark Deal as Lost" color="#DC2626" onClose={() => setShowLostModal(false)} />
                        <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div>
                                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', marginBottom: 4, letterSpacing: '0.04em' }}>Reason</label>
                                <select value={lostReason} onChange={e => setLostReason(e.target.value)}
                                    style={{ width: '100%', fontSize: 11.5, border: '1px solid #E5E7EB', borderRadius: 5, padding: '5px 8px', outline: 'none', fontFamily: 'inherit' }}>
                                    {LOST_REASONS.map(r => <option key={r}>{r}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', marginBottom: 4, letterSpacing: '0.04em' }}>Remarks</label>
                                <textarea value={lostRemarks} onChange={e => setLostRemarks(e.target.value)} rows={3}
                                    placeholder="Additional details…"
                                    style={{ width: '100%', fontSize: 11.5, border: '1px solid #E5E7EB', borderRadius: 5, padding: '5px 8px', outline: 'none', resize: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                            </div>
                        </div>
                        <ModalFoot>
                            <BtnCancel onClick={() => setShowLostModal(false)} />
                            <BtnPrimary onClick={handleConfirmLost} label="Confirm Loss" color="#DC2626" />
                        </ModalFoot>
                    </ModalBox>
                </Overlay>
            )}

            {showSoldModal && (
                <Overlay>
                    <ModalBox style={{ maxWidth: 320, textAlign: 'center' }}>
                        <div style={{ padding: '18px 18px 0' }}>
                            <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                                <Trophy size={20} color="#059669" />
                            </div>
                            <p style={{ fontWeight: 700, fontSize: 13, color: '#111827', margin: '0 0 5px' }}>Confirm as Sold?</p>
                            <p style={{ fontSize: 11, color: '#6B7280', margin: 0, lineHeight: 1.6 }}>Make sure token payment is verified before confirming.</p>
                        </div>
                        <ModalFoot>
                            <BtnCancel onClick={() => setShowSoldModal(false)} />
                            <BtnPrimary onClick={confirmSold} label="Yes, Confirm" color="#059669" />
                        </ModalFoot>
                    </ModalBox>
                </Overlay>
            )}

            {validationMsg && (
                <Overlay>
                    <ModalBox style={{ maxWidth: 320, textAlign: 'center' }}>
                        <div style={{ padding: '18px 18px 0' }}>
                            <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 9px' }}>
                                <AlertCircle size={17} color="#D97706" />
                            </div>
                            <p style={{ fontWeight: 700, fontSize: 12.5, color: '#111827', margin: '0 0 5px' }}>Action Required</p>
                            <p style={{ fontSize: 11, color: '#6B7280', margin: 0, lineHeight: 1.6 }}>{validationMsg}</p>
                        </div>
                        <ModalFoot>
                            <BtnPrimary onClick={() => setValidationMsg(null)} label="Got it" color="#374151" />
                        </ModalFoot>
                    </ModalBox>
                </Overlay>
            )}

            {/* Toast — bottom-right */}
            {toast && (
                <div style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999, background: 'var(--bg-card, #fff)', border: '1px solid var(--border-card, #E5E7EB)', borderRadius: 8, minWidth: 240, overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.10)' }}>
                    <style>{`@keyframes crmSlideUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}`}</style>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', paddingRight: 32, position: 'relative', animation: 'crmSlideUp 0.2s ease' }}>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: toast.type === 'success' ? '#D1FAE5' : '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {toast.type === 'success' ? <CheckCircle size={12} color="#059669" /> : <AlertCircle size={12} color="#DC2626" />}
                        </div>
                        <span style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--text-main, #374151)' }}>{toast.message}</span>
                        <button onClick={() => setToast(null)} style={{ position: 'absolute', top: 7, right: 7, background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 2 }}>
                            <X size={12} />
                        </button>
                    </div>
                    <div style={{ height: 2, background: toast.type === 'success' ? '#D1FAE5' : '#FEE2E2' }}>
                        <style>{`@keyframes crmShrink{from{width:100%}to{width:0%}}`}</style>
                        <div style={{ height: '100%', background: toast.type === 'success' ? '#059669' : '#DC2626', animation: 'crmShrink 3.5s linear forwards' }} />
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── NAV CHIP ────────────────────────────────────────────────────────────────
const NavChip = ({ icon, label }) => (
    <button style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 5, padding: '3px 8px', fontSize: 11, fontWeight: 500, color: '#374151', cursor: 'pointer', fontFamily: 'inherit' }}>
        {icon}
        <span>{label}</span>
    </button>
);

export default CRMDetailPage;